import {
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { ReactiveComponent } from '@app/shared/components/reactive-component';
import { FocusableElement } from '@app/shared/directives/autofocus.directive';
import { TranscludeDirective } from '@app/shared/directives/transclude.directive';
import { TypeaheadBasketType } from '@app/shared/models/typeahead-basket-type';
import { TypeaheadBasketServiceFactory } from '@app/shared/services/typeahead-basket-service-factory.service';
import {
  TargetMetaData,
  TypeaheadBasketService,
  TypeaheadEntry,
} from '@app/shared/services/typeahead-basket.service';
import { loggerFor } from '@app/utils/logger';
import { logValues } from '@app/utils/rxjs';
import { isBlank, isNotBlank } from '@app/utils/string';
import { FeatureTargetValidator } from '@business/feature/validators/feature-target-validator';
import { NgSelectComponent } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { concat, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';

const VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => TargetComponent),
  multi: true,
};

const VALIDATOR_ACCESSOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => TargetComponent),
  multi: true,
};

@UntilDestroy()
@Component({
  selector: 'portal-target',
  templateUrl: './target.component.html',
  styles: [],
  providers: [VALUE_ACCESSOR, VALIDATOR_ACCESSOR],
  host: {
    class: 'portal-target',
  },
  standalone: false,
})
export class TargetComponent
  extends ReactiveComponent
  implements OnChanges, OnInit, Validator, FocusableElement
{
  @Input()
  basketName: string = 'GLOBAL';

  @Input()
  allowCustomValue: boolean = true;

  @Input()
  customOrder: string[] | null = null;

  @Input()
  editableTypes: string[] | 'all' = ['phoneBook', 'services'];

  @Input()
  hideDescription: boolean = false;

  @Input()
  placeholder: string = 'features.target.placeholder';

  @Input()
  showInitial: (TypeaheadBasketType | string)[] = [
    'personal',
    'announcements',
    'custom',
    'phoneBook',
    'services',
    // 'parallelRingingSpecific',
  ];

  @Input()
  typeToSearch?: string;

  @Input()
  value: string;

  @Input()
  validationDisabled: boolean = false;

  @Input()
  virtualScroll: boolean = true;

  @Input()
  additionalEntriesAfterPersonal: boolean = false;

  @Input()
  additionalTypeaheadEntries: TypeaheadEntry<any, TargetMetaData>[] = [];

  // 'text' value indicates that the selected value is shown without select box
  @Input()
  readonly: boolean | 'text' = false;

  @Input()
  clearable: boolean = true;

  @Output()
  onTargetChanged: EventEmitter<string> = new EventEmitter();

  @ContentChildren(TranscludeDirective)
  transclusions!: QueryList<TranscludeDirective>;

  @ViewChild('selectBox', { static: true })
  select: NgSelectComponent;

  /**
   * Entries that are shown in the ng-select
   */
  public items$: Observable<TypeaheadEntry[]> = new Subject();

  /**
   * Class specific logger
   */
  private logger = loggerFor('FeatureTargetComponent');

  /**
   * Subject that is updated when the user changes the search term of the ng-select
   */
  public typeahead$: Subject<string> = new Subject<string>();

  @HostBinding('class.portal-target--editable-search-term')
  public editableSearchTerm: boolean = false;

  public selectedEntry?: TypeaheadEntry;

  /**
   * Validation state
   */
  public valid: boolean = true;

  private get typeaheadBasket(): TypeaheadBasketService {
    return this.typeaheadBasketFactory.get(this.basketName);
  }

  /**
   * Validator instance that validates against all currently possible values. Is recreated when typeahead values change
   */
  private validator: (_: string) => boolean = FeatureTargetValidator([], false);

  constructor(
    private translateService: TranslateService,
    private typeaheadBasketFactory: TypeaheadBasketServiceFactory
  ) {
    super();
    this.translateService.onLangChange.pipe(untilDestroyed(this)).subscribe(() => {
      this.initialiseTypeahead();
      this.initialiseValidation();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('customOrder' in changes && changes.customOrder.firstChange === false) {
      this.typeaheadBasket.setCustomOrder(this.customOrder).refreshAll();
    }

    // Required in case undefined/null is passed to the @Input showInitial parameter
    if ('showInitial' in changes) {
      this.showInitial = changes.showInitial.currentValue ?? [
        'personal',
        'announcements',
        'custom',
        'phoneBook',
        'services',
        // 'parallelRingingSpecific',
      ];
    }

    this.initialiseTypeahead();
    this.initialiseValidation();
  }

  ngOnInit() {
    this.typeaheadBasket.setCustomOrder(this.customOrder).refreshAll();
  }

  focus(): void {
    this.select?.focus();
  }

  private emitValue() {
    this.logger.debug('Emitting new value', this.value);

    // Update the reactive form property
    this.onChange(this.value);

    // Emit validation changed
    this.onValidatorChanged();

    // Emit the on changed event for classic usage
    this.onTargetChanged.emit(this.value);
  }

  /**
   * Filter that is applied on the typeahead on query change
   * @param term
   */
  private filterTypeaheadEntries(term: string): (_: TypeaheadEntry) => boolean {
    const lowerTerm = term.toLowerCase();

    return (v) => v.key.indexOf(lowerTerm) > -1;
  }

  private initialiseTypeahead() {
    this.logger.debug('Typeahead values changed, refreshing ng-select component');

    const additionalTypeaheadEntriesPrepared = this.additionalTypeaheadEntries.map((entry) => {
      const labelTranslated = this.translateService.instant(entry.label);
      const updatedEntry = {
        ...entry,
        label: labelTranslated,
        // if filtering key is not set then use the translated label value
        key: !!entry.key ? entry.key : labelTranslated.toLowerCase(),
      };

      if (updatedEntry.meta != null && updatedEntry.meta.optionName == null) {
        updatedEntry.meta.optionName = labelTranslated;
      }

      return updatedEntry;
    });

    const initialValues = this.getInitialValues(additionalTypeaheadEntriesPrepared);

    const typeToSearch = {
      disabled: true,
      key: undefined,
      value: undefined,
      label: this.translateService.instant('features.target.typeToSearch'),
    };

    // Look for a match in the basket
    let match = this.typeaheadBasket.all.find((entry) => entry.value === this.value);

    if (match !== undefined && initialValues.some((value) => value === match) === false) {
      // If a match exists and the initial values do not contain it already we add it to the initial
      // list of possible values so ng-select shows it
      initialValues.push(match);
    } else if (
      this.value != null &&
      !initialValues.find((entry: TypeaheadEntry) => entry.value === this.value)
    ) {
      const temporaryItem = {
        group: undefined,
        label: this.value,
        key: this.value,
        value: this.value,
      };

      // If no match exists we create a temporary entry for ng-select to display
      initialValues.unshift(temporaryItem);
    } else if (initialValues.length === 0) {
      initialValues.unshift(typeToSearch);
    }

    const initialValues$ = of(initialValues);
    const typeaheadEntries$: Observable<TypeaheadEntry[]> = this.typeahead$.pipe(
      distinctUntilChanged(),
      untilDestroyed(this),
      switchMap((term: string) => {
        let values = initialValues;

        if (!isBlank(term)) {
          values = this.typeaheadBasket.all
            .concat(additionalTypeaheadEntriesPrepared)
            .filter(this.filterTypeaheadEntries(term))
            .slice(0, 20);

          if (this.allowCustomValue === true) {
            values.unshift({
              group: undefined,
              label: term,
              key: term,
              meta: {
                description: this.translateService.instant('features.target.useInputAsValue', {
                  term,
                }),
                editable: true,
              },
              value: term,
            });
          }
        }

        return of(values);
      })
    );

    this.items$ = concat(initialValues$, typeaheadEntries$).pipe(logValues());

    this.selectedEntry = this.typeaheadBasket.all
      .concat(this.additionalTypeaheadEntries)
      .find((entry) => entry.value === this.value);
  }

  public initialiseValidation() {
    this.logger.debug('Typeahead values changed, refreshing validation');

    const possibleValues = this.typeaheadBasket.announcements
      .concat(this.typeaheadBasket.personal)
      .concat(this.additionalTypeaheadEntries)
      .map((_) => _.value);

    this.validator = FeatureTargetValidator(possibleValues, false);
    this.runValidation();
  }

  /**
   * ngSelect was closed
   */
  public onClose() {
    this.typeahead$.next(undefined);
  }

  /**
   * Value from ngSelect changed
   * @param value
   */
  public onValueChanged(value: any) {
    this.logger.debug('ng-select emitted value', value);

    // ng-select emits a plain object so we cannot directly type the value variable
    const actualValue: null | TypeaheadEntry = value;

    this.selectedEntry = value;

    if (actualValue != null) {
      // value.label in case this is a new entry
      this.value = actualValue.value || actualValue.label;
    } else {
      this.value = undefined;
    }

    this.updateEditableSearchTerm();
    this.initialiseTypeahead();
    this.onTouched();
    this.runValidation();
    this.emitValue();

    this.refreshNgSelect();
  }

  private runValidation() {
    if (this.validationDisabled === false) {
      this.logger.debug('Running validation of', this.value);

      this.valid = this.validator(this.value);
      this.onValidatorChanged();

      this.logger.debug('Feature target is now', this.valid ? 'valid' : 'invalid');
    } else {
      this.logger.debug('Internal feature target validation is disabled');
    }
  }

  trackByFn(item: TypeaheadEntry) {
    return item.key ?? item.group;
  }

  updateEditableSearchTerm() {
    if (this.editableTypes === 'all') {
      this.editableSearchTerm = true;
    } else if (this.selectedEntry?.meta?.group != null) {
      this.editableSearchTerm = this.editableTypes.includes(this.selectedEntry.meta.group);
    } else if (this.selectedEntry != null) {
      this.editableSearchTerm = this.selectedEntry.meta?.editable ?? false;
    } else {
      this.editableSearchTerm = isNotBlank(this.value);
    }
  }

  // ControlValueAccessor functions
  validate(control: AbstractControl): ValidationErrors | null {
    let validationErrors = null;

    if (!this.valid) {
      validationErrors = {
        custom: 'features.target.invalid',
        customParameters: { target: this.value },
      };
    }

    this.logger.debug('Validator called, target is now', validationErrors !== null ? 'invalid' : 'valid');

    return validationErrors;
  }

  writeValue(obj: any): void {
    this.logger.debug('Reactive property changed', obj);

    this.value = obj;

    this.initialiseTypeahead();
    this.initialiseValidation();
    this.runValidation();
    this.updateEditableSearchTerm();

    this.refreshNgSelect();
  }

  selectedItemsAsText(items: { label?: string }[]): string {
    return items.map((x) => x.label || '').join();
  }

  private getInitialValues(additionalTypeaheadEntriesPrepared: TypeaheadEntry[]): TypeaheadEntry[] {
    const initialValues: TypeaheadEntry[] = [];

    if (this.additionalEntriesAfterPersonal === false) {
      initialValues.push(...additionalTypeaheadEntriesPrepared);
    }

    for (const index of this.showInitial) {
      if (index !== 'custom' && this.typeaheadBasket[index] !== undefined) {
        initialValues.push(...this.typeaheadBasket[index]);
      } else if (this.typeaheadBasket.custom[index] != null) {
        initialValues.push(...this.typeaheadBasket.custom[index]);
      }
    }

    if (this.additionalEntriesAfterPersonal) {
      initialValues.push(...additionalTypeaheadEntriesPrepared);
    }

    return initialValues;
  }

  /**
   * Currently, there is an issue with the ng-select (https://github.com/ng-select/ng-select/issues/1710)
   * that requires this workaround. We can remove this after the associated pull-request is merged.
   */
  private refreshNgSelect(): void {
    setTimeout(() => {
      if (this.select) {
        (this.select as any)._setSearchTermFromItems();

        if (this.editableSearchTerm === false) {
          (this.select as any)._clearSearch();
        }

        this.select.detectChanges();
      }
    });
  }

  /**
   * Currently there is an issue with ng-select and virtualScroll if:
   *
   *  1. there are not enough items to fill the select dropdown panel (therefore the panel will be
   *     smaller than the max height)
   *  2. the displayed items have different heights
   *
   * If those two cases appear together, then the dropdown panel will be incorrectly sized. The
   * following is a simple fix (by setting the height manually) that will set the height of the
   * panel to the total height of all displayed elements. This should work in most cases and since
   * ng-select is more or less feature-complete and will therefore probably not change its dom, it
   * should also be pretty future-proof.
   *
   * @param $event
   */
  onSearch($event: { term: string; items: any[] }): void {
    setTimeout(() => {
      const panel = this.select.element.querySelector('.ng-dropdown-panel') as HTMLElement | null;

      if (panel != null && panel.offsetHeight < 240) {
        const allItems: NodeListOf<HTMLElement> =
          this.select.element.querySelectorAll('.scrollable-content > *');

        const totalHeight = Array.from(allItems)
          .map((element) => element.offsetHeight)
          .reduce((a, b) => a + b, 0);

        const paddingElement: HTMLElement | null = panel.querySelector('.total-padding');

        if (paddingElement != null) {
          paddingElement.style.height = `${totalHeight}px`;
        }
      }
    }, 0);
  }
}
